import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  appKeySchema,
  errorSchema,
  type ErrorCode,
  localeSchema,
  MIN_PASSWORD_LENGTH,
} from '../common';
import { sessionUserSchema, userSchema } from '../entities';

const c = initContract();

const passwordSchema = z.string().min(MIN_PASSWORD_LENGTH, {
  message: 'password_too_short' satisfies ErrorCode,
});

export const authContract = c.router(
  {
    /**
     * Issues a short-lived access token in the body and the refresh token as an
     * httpOnly cookie — the cookie never appears in this contract because the
     * client neither reads nor sends it by hand.
     */
    login: {
      method: 'POST',
      path: '/login',
      body: z.object({
        login: z.string().min(1),
        password: z.string().min(1),
      }),
      responses: {
        200: z.object({
          accessToken: z.string(),
          user: userSchema,
          apps: z.array(appKeySchema),
        }),
        401: errorSchema,
      },
      summary: 'Exchange credentials for an access token + refresh cookie',
    },

    /** Rotates the refresh row and mints a new access token. */
    refresh: {
      method: 'POST',
      path: '/refresh',
      body: c.noBody(),
      responses: {
        200: z.object({ accessToken: z.string() }),
        401: errorSchema,
      },
      summary: 'Rotate the refresh cookie for a fresh access token',
    },

    logout: {
      method: 'POST',
      path: '/logout',
      body: c.noBody(),
      responses: { 204: c.noBody(), 401: errorSchema },
      summary: 'Revoke the current refresh token and clear the cookie',
    },

    me: {
      method: 'GET',
      path: '/me',
      responses: { 200: sessionUserSchema, 401: errorSchema },
      summary: 'Current user, their app grants, and the release version',
    },

    /**
     * `newPasswordConfirm` stays server-side rather than becoming a pure client
     * check — it is the behaviour the form has today and it costs one comparison.
     */
    changePassword: {
      method: 'POST',
      path: '/password',
      body: z
        .object({
          currentPassword: z.string().min(1, {
            message: 'current_password_required' satisfies ErrorCode,
          }),
          newPassword: passwordSchema,
          newPasswordConfirm: z.string(),
        })
        .refine((b) => b.newPassword === b.newPasswordConfirm, {
          message: 'passwords_mismatch' satisfies ErrorCode,
          path: ['newPasswordConfirm'],
        }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchema,
        401: errorSchema,
      },
      summary: 'Change own password; revokes every refresh token for the user',
    },

    /** Always the caller's own row — no app grant, no user id in the path. */
    setLocale: {
      method: 'PUT',
      path: '/locale',
      body: z.object({ locale: localeSchema }),
      responses: { 200: userSchema, 400: errorSchema, 401: errorSchema },
      summary: 'Set own UI language',
    },
  },
  { pathPrefix: '/auth' },
);
